import { useState } from "react";

import { ESCOPOS_PADRAO_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { useTableSettings } from "@/ds";
import { useMediaQuery } from "@/hooks";
import type { HubTableDataTableProps } from "@/ds";
import { alterarSituacaoEscopoPadrao } from "@/modules/financeiro-configuracoes";
import type { EscopoPadraoLinha, VisaoEscopoPadrao } from "@/modules/financeiro-configuracoes";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { FINANCEIRO_MOBILE_QUERY } from "../financeiro-configuracoes.constants";
import { useAtivarDireto } from "../hooks/use-ativar-direto";
import {
  COLUNAS_FIXAS_DESKTOP,
  COLUNAS_POR_VISAO,
  ESCOPOS_PADRAO_ACOES_KEY,
  ESCOPOS_PADRAO_FILTRO_EMBARCACAO_ROTULO,
  ESCOPOS_PADRAO_FILTRO_GRUPO_CLASSE_ROTULO,
  ESCOPOS_PADRAO_FILTRO_GRUPO_ROTULO,
  ESCOPOS_PADRAO_FILTRO_TIPO_OBRA_ROTULO,
  ESCOPOS_PADRAO_FILTRO_TODOS,
  ESCOPOS_PADRAO_FILTRO_TODOS_ROTULO,
  ESCOPOS_PADRAO_MODAIS_DISPONIVEIS,
  ESCOPOS_PADRAO_NOVA_VERSAO_SEM_VERSAO_DICA,
  ESCOPOS_PADRAO_VERSAO_ANTERIOR_AVISO,
  ESCOPOS_PADRAO_VERSAO_ANTERIOR_DICA,
  rotuloVersao,
  ESCOPOS_PADRAO_ATIVAR_ERRO,
  ESCOPOS_PADRAO_TABLE_LABEL,
  ESCOPOS_PADRAO_TOASTS,
  ESCOPOS_PADRAO_VIGENTE_SUFIXO,
  TABLE_MIN_WIDTH_POR_VISAO,
  isCelulaOrdenavel,
  VISOES,
} from "./escopos-padrao.constants";
import type { EscoposPadraoColumnKey } from "./escopos-padrao.constants";
import { gruposDaVisao, tiposDaVisao } from "./escopos-padrao.filtros";
import { montarCelulas } from "./escopos-padrao.linhas";
import { FILTROS_VAZIOS, useEscoposPadraoLista } from "./hooks/use-escopos-padrao-lista";
import type { FiltrosEscoposPadrao } from "./hooks/use-escopos-padrao-lista";
import { useEscoposPadraoLookups } from "./hooks/use-escopos-padrao-lookups";
import type {
  AcaoEscopoPadrao,
  AcaoPendente,
  EscopoPadraoTabelaLinha,
  EscoposPadraoConteudo,
  EscoposPadraoOpcao,
  EscoposPadraoViewModel,
} from "./types-escopos-padrao";

const todos = (opcoes: EscoposPadraoOpcao[]): EscoposPadraoOpcao[] => [
  { label: ESCOPOS_PADRAO_FILTRO_TODOS_ROTULO, value: ESCOPOS_PADRAO_FILTRO_TODOS },
  ...opcoes,
];

export function useEscoposPadrao(): EscoposPadraoViewModel {
  const { hasPermissao } = usePermissoes();
  const podeVer = hasPermissao(HUB_PERMISSOES.FINANCEIRO_VIEW_CONFIGURACAO);
  const podeEditar = hasPermissao(HUB_PERMISSOES.FINANCEIRO_EDIT_CONFIGURACAO);
  const [versaoEscolhida, setVersaoEscolhida] = useState<string | null>(null);
  const [acaoPendente, setAcaoPendente] = useState<AcaoPendente | null>(null);

  const apoio = useEscoposPadraoLookups({ podeVer, comMoedas: podeEditar });
  const versoes = apoio.versoes.items;
  const versaoAtual =
    versoes.find((v) => v.versao === versaoEscolhida) ??
    versoes.find((v) => v.vigente) ??
    versoes[0] ??
    null;
  const naVigente = versaoAtual?.vigente === true;

  const mobile = useMediaQuery(FINANCEIRO_MOBILE_QUERY);

  const lista = useEscoposPadraoLista(versaoAtual?.versao ?? null);
  const { visao, filtros } = lista;
  const settings = useTableSettings<EscoposPadraoColumnKey>({
    densityKey: ESCOPOS_PADRAO_TABLE_DENSITY_KEY,
    allColumns: [...COLUNAS_POR_VISAO[visao].map(({ key }) => key), ESCOPOS_PADRAO_ACOES_KEY],
  });

  const tipos = tiposDaVisao(apoio.tiposObra, visao);
  const grupos = gruposDaVisao(apoio.estrutura, visao, filtros.tipoObraId);

  const normalizar = (alvo: VisaoEscopoPadrao, atuais: FiltrosEscoposPadrao) => {
    const tipoObraId = tiposDaVisao(apoio.tiposObra, alvo).some(
      (t) => t.value === atuais.tipoObraId,
    )
      ? atuais.tipoObraId
      : ESCOPOS_PADRAO_FILTRO_TODOS;
    const grupoId = gruposDaVisao(apoio.estrutura, alvo, tipoObraId).some(
      (g) => g.value === atuais.grupoId,
    )
      ? atuais.grupoId
      : ESCOPOS_PADRAO_FILTRO_TODOS;
    return { ...atuais, tipoObraId, grupoId };
  };

  const selecionarVisao = (alvo: VisaoEscopoPadrao) => {
    lista.setVisao(alvo);
    lista.setFiltros(normalizar(alvo, filtros));
  };
  const alterarFiltro = (campo: keyof FiltrosEscoposPadrao) => (value: string) =>
    lista.setFiltros(normalizar(visao, { ...filtros, [campo]: value }));

  const ativarDireto = useAtivarDireto<EscopoPadraoLinha>({
    enviar: (escopo, idempotencyKey) =>
      alterarSituacaoEscopoPadrao(escopo.id, "ATIVO", { idempotencyKey }),
    sucesso: (escopo) =>
      ESCOPOS_PADRAO_TOASTS.situacao.ativar(escopo.equipamento?.tag || escopo.escopo),
    aoConcluir: lista.reload,
    erroPadrao: ESCOPOS_PADRAO_ATIVAR_ERRO,
  });

  const acoesDaLinha = (escopo: EscopoPadraoLinha): EscopoPadraoTabelaLinha["acoes"] => {
    if (!podeEditar || !naVigente) return null;
    const alternar: AcaoEscopoPadrao = escopo.situacao === "ATIVO" ? "inativar" : "ativar";
    const nome = escopo.escopo || escopo.equipamento?.tag || escopo.tipoObra.codigo;
    const montar = (tipo: AcaoEscopoPadrao, verbo: string) => ({
      tipo,
      label: `${verbo} ${nome}`,
      testId: `escopos-padrao-${tipo}-${escopo.id}`,
      desabilitada: !ESCOPOS_PADRAO_MODAIS_DISPONIVEIS[tipo === "editar" ? "editar" : "situacao"],
      executar: () =>
        tipo === "ativar" ? void ativarDireto(escopo) : setAcaoPendente({ tipo, escopo }),
    });
    const situacao = montar(alternar, alternar === "inativar" ? "Inativar" : "Ativar");
    return escopo.situacao === "ATIVO" ? [montar("editar", "Editar"), situacao] : [situacao];
  };

  const linhas = lista.items.map<EscopoPadraoTabelaLinha>((escopo) => ({
    id: escopo.id,
    escopo,
    celulas: montarCelulas(escopo),
    inativa: escopo.situacao === "INATIVO",
    acoes: acoesDaLinha(escopo),
  }));

  const filtrosVm: EscoposPadraoViewModel["filtros"] = [
    {
      rotulo: ESCOPOS_PADRAO_FILTRO_TIPO_OBRA_ROTULO,
      value: filtros.tipoObraId,
      options: todos(tipos),
      onChange: alterarFiltro("tipoObraId"),
      testId: "escopos-padrao-filtro-tipo-obra",
    },
    ...(visao === "OUTROS"
      ? []
      : [
          {
            rotulo:
              visao === "CLASSE"
                ? ESCOPOS_PADRAO_FILTRO_GRUPO_CLASSE_ROTULO
                : ESCOPOS_PADRAO_FILTRO_GRUPO_ROTULO,
            value: filtros.grupoId,
            options: todos(grupos),
            onChange: alterarFiltro("grupoId"),
            testId: "escopos-padrao-filtro-grupo",
          },
        ]),
    {
      rotulo: ESCOPOS_PADRAO_FILTRO_EMBARCACAO_ROTULO,
      value: filtros.embarcacaoId,
      options: todos(apoio.embarcacoes.map(({ id, nome }) => ({ label: nome, value: id }))),
      onChange: alterarFiltro("embarcacaoId"),
      testId: "escopos-padrao-filtro-embarcacao",
    },
  ];

  const hasFilters =
    lista.mostrarInativos ||
    filtros.tipoObraId !== ESCOPOS_PADRAO_FILTRO_TODOS ||
    filtros.grupoId !== ESCOPOS_PADRAO_FILTRO_TODOS ||
    filtros.embarcacaoId !== ESCOPOS_PADRAO_FILTRO_TODOS;

  const fecharAcao = () => setAcaoPendente(null);
  const semVersao = versaoAtual === null;
  let conteudo: EscoposPadraoConteudo = "lista";
  if (!podeVer) conteudo = "oculto";
  else if (apoio.versoes.status === "forbidden") conteudo = "sem-permissao";
  else if (apoio.versoes.status === "error") conteudo = "erro";
  else if (apoio.versoes.status === "idle" || apoio.versoes.status === "loading") {
    conteudo = "carregando";
  } else if (semVersao) conteudo = "sem-versao";

  const dataTableProps: HubTableDataTableProps = {
    scrollable: true,
    tableStyle: { minWidth: TABLE_MIN_WIDTH_POR_VISAO[visao] },
    "aria-label": ESCOPOS_PADRAO_TABLE_LABEL,
  };

  return {
    conteudo,
    mensagem: apoio.versoes.errorMessage,
    recarregarVersoes: apoio.versoes.reload,
    versao: {
      value: versaoAtual?.versao ?? "",
      options: versoes.map((v) => ({
        label: v.vigente
          ? `${rotuloVersao(v.versao)} ${ESCOPOS_PADRAO_VIGENTE_SUFIXO}`
          : rotuloVersao(v.versao),
        value: v.versao,
      })),
      onChange: setVersaoEscolhida,
    },
    visao,
    visoes: VISOES.map(({ valor, rotulo }) => ({
      valor,
      rotulo,
      testId: `escopos-padrao-visao-${valor}`,
    })),
    selecionarVisao,
    filtros: filtrosVm,
    mostrarInativos: lista.mostrarInativos,
    alternarInativos: () => lista.setMostrarInativos((atual) => !atual),
    novoEscopo: podeEditar
      ? {
          desabilitado: !ESCOPOS_PADRAO_MODAIS_DISPONIVEIS.novo || semVersao || !naVigente,
          dica: semVersao || naVigente ? null : ESCOPOS_PADRAO_VERSAO_ANTERIOR_DICA,
          executar: () => setAcaoPendente({ tipo: "novo" }),
        }
      : undefined,
    novaVersao: podeEditar
      ? {
          desabilitado: !ESCOPOS_PADRAO_MODAIS_DISPONIVEIS.novaVersao || semVersao || !naVigente,
          dica: semVersao
            ? ESCOPOS_PADRAO_NOVA_VERSAO_SEM_VERSAO_DICA
            : naVigente
              ? null
              : ESCOPOS_PADRAO_VERSAO_ANTERIOR_DICA,
          executar: () => setAcaoPendente({ tipo: "nova-versao" }),
        }
      : undefined,
    avisoSomenteLeitura:
      versaoAtual && !naVigente
        ? ESCOPOS_PADRAO_VERSAO_ANTERIOR_AVISO(
            rotuloVersao(versoes.find((v) => v.vigente)?.versao ?? ""),
          )
        : null,
    tabela: {
      items: linhas,
      total: lista.total,
      status: lista.status,
      errorMessage: lista.errorMessage,
      isFirstLoad: lista.status === "loading" && linhas.length === 0,
      reload: lista.reload,
      table: lista.tabela,
      settings,
      dataTableProps,
      colunas: COLUNAS_POR_VISAO[visao].map((coluna) => ({
        ...coluna,
        ordenavel: isCelulaOrdenavel(coluna.key),
      })),
      colunasFixas: mobile ? 1 : COLUNAS_FIXAS_DESKTOP[visao],
      mostrarAcoes: podeEditar && naVigente,
      acoesFixas: !mobile,
      hasFilters,
      onClear: hasFilters
        ? () => {
            lista.setFiltros(FILTROS_VAZIOS);
            lista.setMostrarInativos(false);
          }
        : undefined,
    },
    acaoPendente,
    fecharAcao,
    dialogo:
      versaoAtual && (acaoPendente?.tipo === "novo" || acaoPendente?.tipo === "editar")
        ? {
            versao: versaoAtual.versao,
            escopo: acaoPendente.tipo === "editar" ? acaoPendente.escopo : undefined,
            tipos: apoio.tiposObra,
            estrutura: apoio.estrutura,
            embarcacoes: apoio.embarcacoes,
            moedas: apoio.moedas,
            onClose: fecharAcao,
            onConcluido: () => {
              fecharAcao();
              lista.reload();
            },
          }
        : null,
    dialogoSituacao:
      versaoAtual && acaoPendente?.tipo === "inativar"
        ? {
            escopo: acaoPendente.escopo,
            versao: versaoAtual.versao,
            onClose: fecharAcao,
            onConcluido: () => {
              fecharAcao();
              lista.setMostrarInativos(true);
              lista.reload();
            },
          }
        : null,
    dialogoNovaVersao:
      acaoPendente?.tipo === "nova-versao"
        ? {
            onClose: fecharAcao,
            onConcluido: (versaoCriada) => {
              fecharAcao();
              setVersaoEscolhida(versaoCriada);
              apoio.versoes.reload();
              lista.reload();
            },
          }
        : null,
  };
}
